import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  listAccompanyShoppingProviders,
  getAccompanyShoppingProviderById,
  updateProvidersStatus,
  updateProviderCertificationStatus,
  updateAccompanyShoppingProvider
} from '../../api/accompanyShoppingProvider';

// 可用状态映射
export const AVAILABILITY_STATUS_MAP = {
  0: '不可用',
  1: '可用'
};

// 认证状态映射
export const CERTIFICATION_STATUS_MAP = {
  0: '未认证',
  1: '认证中',
  2: '已认证',
  3: '认证失败'
};

// 分页查询陪逛服务提供者的异步 thunk
export const fetchAccompanyShoppingProviders = createAsyncThunk(
  'accompanyShoppingProvider/fetchProviders',
  async (params, { rejectWithValue }) => {
    try {
      const response = await listAccompanyShoppingProviders(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 获取单个陪逛服务提供者详情的异步 thunk
export const fetchAccompanyShoppingProviderDetail = createAsyncThunk(
  'accompanyShoppingProvider/fetchProviderDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getAccompanyShoppingProviderById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 批量更新陪逛服务提供者状态的异步 thunk
export const batchUpdateProvidersStatus = createAsyncThunk(
  'accompanyShoppingProvider/batchUpdateStatus',
  async ({ providerIds, status }, { rejectWithValue }) => {
    try {
      const response = await updateProvidersStatus(providerIds, status);
      return { providerIds, status, updatedCount: response.data };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 更新陪逛服务提供者认证状态的异步 thunk
export const updateProviderCertification = createAsyncThunk(
  'accompanyShoppingProvider/updateCertification',
  async ({ providerId, certificationStatus }, { rejectWithValue }) => {
    try {
      const response = await updateProviderCertificationStatus(providerId, certificationStatus);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 更新陪逛服务提供者信息的异步 thunk
export const updateProvider = createAsyncThunk(
  'accompanyShoppingProvider/updateProvider',
  async (providerData, { rejectWithValue }) => {
    try {
      const response = await updateAccompanyShoppingProvider(providerData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const accompanyShoppingProviderSlice = createSlice({
  name: 'accompanyShoppingProvider',
  initialState: {
    providers: [],
    total: 0,
    current: 1,
    pageSize: 10,
    currentProvider: null,
    selectedProviderIds: [],
    loading: false,
    error: null
  },
  reducers: {
    setSelectedProviderIds: (state, action) => {
      state.selectedProviderIds = action.payload;
    }
  },
  extraReducers: (builder) => {
    // 处理分页查询陪逛服务提供者的 reducer
    builder.addCase(fetchAccompanyShoppingProviders.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAccompanyShoppingProviders.fulfilled, (state, action) => {
      state.loading = false;
      state.providers = action.payload.records || [];
      state.total = action.payload.total || 0;
      state.current = action.payload.current || 1;
      state.pageSize = action.payload.size || 10;
    });
    builder.addCase(fetchAccompanyShoppingProviders.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 处理获取提供者详情的 reducer
    builder.addCase(fetchAccompanyShoppingProviderDetail.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAccompanyShoppingProviderDetail.fulfilled, (state, action) => {
      state.loading = false;
      state.currentProvider = action.payload;
    });
    builder.addCase(fetchAccompanyShoppingProviderDetail.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 处理批量更新提供者状态的 reducer
    builder.addCase(batchUpdateProvidersStatus.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(batchUpdateProvidersStatus.fulfilled, (state, action) => {
      state.loading = false;
      const { providerIds, status } = action.payload;
      
      // 更新列表中的提供者状态
      state.providers = state.providers.map(provider => 
        providerIds.includes(provider.id) ? { ...provider, availabilityStatus: status } : provider
      );

      // 如果当前提供者在更新列表中，也更新其状态
      if (state.currentProvider && providerIds.includes(state.currentProvider.id)) {
        state.currentProvider.availabilityStatus = status;
      }

      // 清空选中的提供者ID
      state.selectedProviderIds = [];
    });
    builder.addCase(batchUpdateProvidersStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 处理更新提供者认证状态的 reducer
    builder.addCase(updateProviderCertification.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateProviderCertification.fulfilled, (state, action) => {
      state.loading = false;
      
      // 更新列表中的提供者认证状态
      const index = state.providers.findIndex(provider => provider.id === action.payload.id);
      if (index !== -1) {
        state.providers[index] = action.payload;
      }

      // 更新当前提供者
      if (state.currentProvider && state.currentProvider.id === action.payload.id) {
        state.currentProvider = action.payload;
      }
    });
    builder.addCase(updateProviderCertification.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 处理更新提供者信息的 reducer
    builder.addCase(updateProvider.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateProvider.fulfilled, (state, action) => {
      state.loading = false;
      
      // 更新列表中的提供者信息
      const index = state.providers.findIndex(provider => provider.id === action.payload.id);
      if (index !== -1) {
        state.providers[index] = action.payload;
      }

      // 更新当前提供者
      if (state.currentProvider && state.currentProvider.id === action.payload.id) {
        state.currentProvider = action.payload;
      }
    });
    builder.addCase(updateProvider.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  }
});

export const { setSelectedProviderIds } = accompanyShoppingProviderSlice.actions;
export default accompanyShoppingProviderSlice.reducer; 